/**
 * The capability seam: what this principal's API answers say the interface may offer.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * THE RULE THIS FILE EXISTS FOR
 *   The UI hides what the server hides. It never decides permissions from who it thinks
 *   is signed in: `/api/me` carries no roles on purpose, because a client that learns
 *   its permissions from a response it can edit has learned nothing. So a screen asks
 *   the API a question and renders the answer, and absence is the answer the predicate
 *   gives counterparties for a configuration object, the same answer it gives for one
 *   that does not exist.
 *
 * THE SWITCH
 *   `configurationEndpoints.live` is the single switch the configuration track (M3
 *   Track E) flips when `GET /api/configuration/terminology` lands. While it is false
 *   the probe performs NO network call and answers absence for everyone, which is both
 *   honest (the endpoint does not exist yet, so calling it would be probing a route the
 *   edge does not serve) and correct (no one sees configuration navigation, for a
 *   counterparty because the predicate will deny, and for everyone else because there
 *   is nothing behind the door yet). When it is true, the probe below is the real call
 *   and needs no further client change: the API's answer decides, per principal.
 */

import { ApiError, apiJson } from "./client";

export const configurationEndpoints = { live: true } as { live: boolean };

/**
 * The probe. Answered without a network call until the configuration track lands its
 * endpoints; see the switch above.
 */
export async function probeConfigurationAccess(token: string): Promise<boolean> {
  if (!configurationEndpoints.live) {
    return false;
  }
  try {
    // The LIST routes answer a non-granted principal 200 [] BY CONTRACT (absence, not
    // an error to argue with), so a list response cannot distinguish "allowed" from
    // "hidden". The audit trail is the route whose encoding carries the predicate: no
    // grant, no endpoint at all (404); a granted principal reads it.
    await apiJson(token, "/configuration/audit", () => undefined);
    return true;
  } catch (error) {
    if (error instanceof ApiError && error.kind === "absence") {
      // A counterparty principal is answered with absence, not 403, and so is this
      // client. No configuration navigation renders for absence, by design.
      return false;
    }
    if (error instanceof ApiError && error.kind === "forbidden") {
      return false;
    }
    throw error;
  }
}

export { probeAuditAccess } from "./audit";
export type { ApiError };
